import {Injectable, Injector} from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TokenService } from 'src/app/shared/services/token.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { TranslocoService } from '@ngneat/transloco';
import { AuthControllerService } from 'src/app/api-client';

const TOKEN_HEADER_KEY = 'Authorization'; // for Spring Boot back-end

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  constructor(
    private _token: TokenService,
    private _auth: AuthControllerService,
    private _notification: NotificationService,
    private _transloco: TranslocoService,
    private injector: Injector
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<Object>> {
    let authReq = req;
    const token = this._token.getToken();
    if (token != null) {
      authReq = this.addTokenHeader(req, token);
    }
    return next.handle(authReq).pipe(
      catchError((error) => {
        if (
          error instanceof HttpErrorResponse &&
          !authReq.url.includes('auth/login') &&
          !authReq.url.includes('auth/refresh') &&
          error.status === 401
        ) {
          return this.handle401Error(authReq, next, error);
        }
        return throwError(error);
      })
    );
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse
  ) {
    const router = this.injector.get(Router);
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      const token = this._token.getRefreshToken();
      if (!token) {
        // Nothing to refresh — a 401 on a public page before any login. Without
        // resetting the flag the interceptor would stay in "refreshing" state for
        // the rest of the app's lifetime and every later 401 would hang on the
        // subject below (which only ever holds null), so propagate instead.
        this.isRefreshing = false;
        return throwError(error);
      }
      return this._auth.refreshToken(token).pipe(
        switchMap((token: any) => {
          this.isRefreshing = false;
          this._token.saveToken(token.accessToken);
          this.refreshTokenSubject.next(token.accessToken);

          return next.handle(this.addTokenHeader(request, token.accessToken));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this._token.signOut();
          router.navigate(['/auth/login'], {
            queryParams: {returnUrl: router.routerState.snapshot.url},
          });
          this._notification.error(
            this._transloco.translate('login.session-expired')
          );
          return throwError(err);
        })
      );
    }
    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenHeader(request, token)))
    );
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    /* for Spring Boot back-end */
    return request.clone({
      headers: request.headers.set(TOKEN_HEADER_KEY, 'Bearer ' + token),
    });
  }
}

export const authInterceptorProviders = [
  {provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true},
];
